import Accordion from 'src/views/pages/manageUser/NewUser/Body/Form/Dealership/Accordion';
import { DealerManagementService } from 'src/services/dealer-management.service';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { getBrandByDealerNumber } from 'src/utils/helpers';
import Header from './components/header';
import { IUser } from 'src/utils/interfaces/user';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import { Permissions } from 'src/utils/enums/permissions.enum';
import Spinner from 'src/components/ui/spinner';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import './styles.scss';

enum ECountries {
    US = 'US',
    CA = 'CA',
}

enum EStatus {
    ACTIVE = 'Active',
    INACTIVE = 'Inactive',
}

interface IDealer {
    dealerNumber: string;
    dealerName: string;
    status: EStatus.ACTIVE | EStatus.INACTIVE;
    fspPermissions: Array<string>;
    country: string;
    manufacturerCode: string | number;
}

interface IDealerPermissionDiff {
    dealerId: string;
    currentPermissions: string[];
    newPermissions: string[];
}

const DealerGroupPermissions = () => {
    const [showMessage, setShowMessage] = useState({
        show: false,
        success: false,
    });
    const { state } = useLocation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatch = useDispatch();

    if (!state?.dealers) {
        return (
            <Navigate
                to='/dashboard/systemadmin/dealer-group-maintenance/search'
                replace
            />
        );
    }

    const dealersData = state.dealers
        .flatMap((group: any) => group.dealers)
        .map((item: any) => item);

    const handleActiveModal = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
            payload: {
                titleContent: 'Are you sure you want to leave?',
                bodyContent:
                    'Any information you’ve entered will not be saved.',
                cancelBtnLabel: 'Cancel',
                confirmBtnLabel: 'Yes, confirm',
                onCancelHandler: cancelhandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    useEffect(() => {
        handleActiveModal();
    }, []);

    const headerBackClickHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: {
                shouldShow: true,
                onCancelHandler: cancelhandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    const cancelhandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: { shouldShow: false },
        });
    };

    const confirmHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
            payload: { shouldShow: false },
        });
        setTimeout(() => {
            navigate('/dashboard/systemadmin/dealer-group-maintenance/');
        }, Number('700'));
    };

    const [permissionsMap, setPermissionsMap] = useState<
        Map<string, Array<string>>
    >(getData());

    function getData(): Map<string, string[]> {
        return new Map(
            dealersData.map((dealer: IDealer) => [
                dealer.dealerNumber,
                Array.isArray(dealer.fspPermissions)
                    ? [...new Set(dealer.fspPermissions)]
                    : [],
            ]),
        );
    }

    const buildDealerPermissionsDiffFromMap = (
        oldDealers: any,
        newPermissionsMap: Map<string, string[]>,
    ): IDealerPermissionDiff[] => {
        const oldMap = new Map<string, string[]>(
            oldDealers
                .flatMap((group: any) => group.dealers)
                .map((d: IDealer) => [
                    d.dealerNumber,
                    Array.isArray(d.fspPermissions)
                        ? [...new Set(d.fspPermissions)]
                        : [],
                ]),
        );

        const result: IDealerPermissionDiff[] = [];

        for (const [dealerId, newPermissions] of newPermissionsMap.entries()) {
            const currentPermissions = oldMap.get(dealerId) ?? [];

            result.push({
                dealerId,
                currentPermissions,
                newPermissions: Array.isArray(newPermissions)
                    ? [...new Set(newPermissions)]
                    : [],
            });
        }

        return result;
    };

    function matchDealerCountry(country: string, dealer: IDealer): boolean {
        if (dealer.country === country) return true;
        return false;
    }

    function matchManufacturerCode(dealer: IDealer): boolean {
        if (!dealer.manufacturerCode) {
            return false;
        }

        return [Number('70'), Number('71')].includes(
            Number(dealer.manufacturerCode),
        );
    }

    function getPermissionsByStatus(dealer: IDealer) {
        if (dealer.status === EStatus.INACTIVE)
            return [
                Permissions.USER_ADMIN,
                Permissions.PAYOFF_QUOTES,
                Permissions.MATURITY_MANAGER,
            ];
        return [
            Permissions.USER_ADMIN,
            Permissions.CREDIT_APPLICATION,
            Permissions.APPLICATION_STATUS_TRACKING,
            Permissions.FUNDING_STATUS,
            Permissions.PAYOFF_QUOTES,
            Permissions.MARKETING_BULLETINS,
            Permissions.DEALER_RESERVES_EFT_NOTIFICATIONS,
            Permissions.MATURITY_MANAGER,
            ...(matchManufacturerCode(dealer) &&
            matchDealerCountry(ECountries.US, dealer)
                ? [Permissions.VW_AUDI_DIRECT]
                : []),
            ...(matchDealerCountry(ECountries.CA, dealer)
                ? [Permissions.DEALER_ELECTRONIC_PAYMENT]
                : []),
            ...(matchDealerCountry(ECountries.US, dealer)
                ? [Permissions.SERVICE_LOANER, Permissions.DEALER_RENTAL]
                : []),
        ];
    }

    const dealerPermissions = dealersData.map(
        (dealer: IDealer, index: number) => {
            return (
                <Accordion
                    dealerId={dealer.dealerNumber}
                    dealerName={dealer.dealerName}
                    key={dealer.dealerNumber}
                    status={dealer.status}
                    permissionsMap={permissionsMap}
                    setPermissionsMap={setPermissionsMap}
                    disabled={false}
                    permissionsList={getPermissionsByStatus(dealer)}
                    newPermissionList={[
                        {
                            name: 'Maturity/Portfolio Manager',
                            value: Permissions.MATURITY_MANAGER,
                        },
                        ...(matchDealerCountry(ECountries.CA, dealer)
                            ? [
                                  {
                                      name: 'Dealer Electronic Payments',
                                      value: Permissions.DEALER_ELECTRONIC_PAYMENT,
                                  },
                              ]
                            : []),
                    ]}
                    isOpenDefault={index === Number('0')}
                />
            );
        },
    );

    let brand = 'vw';
    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    if (currentDealers && dealerDataInfo) {
        const { dealer } = dealerDataInfo;
        brand = getBrandByDealerNumber(dealer.brand, dealer.dealerNumber);
    }

    async function onsubmit() {
        setShowMessage((values) => {
            return { ...values, show: false, success: false };
        });
        setIsLoading(true);
        const service = new DealerManagementService();
        try {
            await service.updateDealerGroupPermissions(
                buildDealerPermissionsDiffFromMap(
                    state.dealers,
                    permissionsMap,
                ),
            );
            setShowMessage((values) => {
                return { ...values, show: true, success: true };
            });
        } catch (error) {
            setShowMessage((values) => {
                return { ...values, show: true, success: false };
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <Header backClickHandler={headerBackClickHandler} />
            <div className='dashboard-content-wrapper'>
                <div className='dealer-group-maintenance dashboard-content-container'>
                    <section className='o-content-section'>
                        {showMessage.show ? (
                            <div className={''}>
                                <Notification
                                    content={
                                        showMessage.success
                                            ? 'Dealer group updated successfully.'
                                            : 'An error occur, please rey again later.'
                                    }
                                    type={
                                        showMessage.success
                                            ? NotificationType.SUCCESS
                                            : NotificationType.ERROR
                                    }
                                    visibility={showMessage.show}
                                    setVisibility={() => {
                                        setShowMessage((values) => {
                                            return {
                                                ...values,
                                                show: false,
                                                success: false,
                                            };
                                        });
                                    }}
                                />{' '}
                            </div>
                        ) : null}
                        <div className='page-title'>
                            <h5>Manage permissions for dealer group</h5>
                        </div>
                        <section className='c-form-section'>
                            <div className='c-form-section__content'>
                                <div className='o-fieldset__row u-mt-x-small u-mb-x-small'>
                                    {dealerPermissions
                                        ? dealerPermissions
                                        : null}
                                </div>
                                <div className='o-layout__item u-1/1 u-1/1@xs'>
                                    <div className='o-fieldset__row button-group'>
                                        <button
                                            className={'c-btn c-btn--secondary'}
                                            type='submit'
                                            disabled={isLoading}
                                            onClick={() => {
                                                headerBackClickHandler();
                                            }}
                                        >
                                            <span className='c-btn__text'>
                                                Cancel
                                            </span>
                                        </button>
                                        <button
                                            className={`c-btn btn--secondary ${brand}`}
                                            type='button'
                                            disabled={isLoading}
                                            onClick={() => {
                                                onsubmit();
                                            }}
                                        >
                                            <span className='c-btn__text'>
                                                {isLoading ? (
                                                    <Spinner />
                                                ) : (
                                                    'Save'
                                                )}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                        <div style={{ gridColumnStart: 2, marginTop: '40px' }}>
                            <FooterPrivacy />
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default DealerGroupPermissions;
